import { useCallback, useEffect, useState } from "react";
import { CalendarOff, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const fmt = (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");

export const ProviderTimeOffPanel = ({ providerId }) => {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ start_date: "", end_date: "", note: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows((await api.get("/attendance/time-off", { params: { provider_id: providerId } })).data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [providerId]);

  useEffect(() => {
    load();
  }, [load]);

  async function add(event) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/attendance/time-off", { provider_id: providerId, ...form });
      setForm({ start_date: "", end_date: "", note: "" });
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  async function remove(row) {
    try {
      await api.delete(`/attendance/time-off/${row.id}`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <div className="provider-time-off" data-testid="provider-time-off-panel">
      <div>
        <strong><CalendarOff size={14} /> Time off</strong>
        <span className="pairing-meta">No no-clock-in task is opened on these dates. Approved Connecteam requests sync in automatically.</span>
      </div>
      {error && <p className="form-message error-message" data-testid="provider-time-off-error">{error}</p>}
      <ul className="provider-time-off-list">
        {(rows || []).map((row) => (
          <li key={row.id} data-testid={`provider-time-off-${row.id}`}>
            <span>{fmt(row.start_date)}{row.end_date !== row.start_date ? ` – ${fmt(row.end_date)}` : ""}{row.note ? ` · ${row.note}` : ""}</span>
            <small className="pairing-meta">{row.source === "connecteam" ? "Connecteam" : `Added by ${row.created_by_name || "admin"}`}</small>
            {row.source !== "connecteam" && <button type="button" className="provider-profile-link" data-testid={`provider-time-off-delete-${row.id}`} onClick={() => remove(row)}><Trash2 size={13} /> Remove</button>}
          </li>
        ))}
        {rows && !rows.length && <li className="pairing-meta" data-testid="provider-time-off-empty">No time off on file.</li>}
      </ul>
      <form className="provider-time-off-form" data-testid="provider-time-off-form" onSubmit={add}>
        <label>Start<input data-testid="provider-time-off-start" required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></label>
        <label>End<input data-testid="provider-time-off-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></label>
        <label>Note<input data-testid="provider-time-off-note" maxLength={300} placeholder="Optional" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></label>
        <button type="submit" className="button button-secondary" data-testid="provider-time-off-add" disabled={busy || !form.start_date}><Plus size={14} /> Add time off</button>
      </form>
    </div>
  );
};
